import React, { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { setAuthorization, onForbidden, onSessionExpired } from "../helpers/api_helper";
import {
  isTokenValid,
  getTokenRemainingTime,
  refreshAccessToken,
  getAuthUser,
} from "../helpers/tokenHelper";
import { GET_MY_ALLOWED_PATHS, GET_MY_PAGE_ACTION_INPUT_CODES } from "../helpers/url_helper";
import { useProfile } from "../Components/Hooks/UserHooks";
import { logoutUser } from "../slices/auth/login/thunk";
import { fetchMyPermissions } from "../slices/permission/thunk";
import { setSessionExpired } from "../slices/permission/reducer";
import PageActionScanner from "../services/pageActionScanner/PageActionScanner";
import { ModulePermission } from "../services/modulePermission/ModulePermission";
import type { RootState } from "../slices";
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('routes');

const AuthProtected = (props: any) => {
  const dispatch: any = useDispatch();
  const location = useLocation();
  const { userProfile, loading, token } = useProfile();
  const permission = useSelector((state: RootState) => state.Permission);

  // Quyền (route/menu + button/input) được nạp 1 lần vào Redux global state — xem mục 9,
  // README_SERCURITY.md. Redux sống trong RAM nên bị mất mỗi khi tải lại trang (F5/Ctrl+F5) hoặc mở
  // lại trình duyệt (permission.loaded về false), phải fetch lại trước khi render children.
  useEffect(() => {
    if (!userProfile || !token) return;
    if (permission.loaded || permission.loading || permission.error) return;
    dispatch(fetchMyPermissions());
  }, [userProfile, token, permission.loaded, permission.loading, permission.error, dispatch]);

  // permission.sessionExpired = true khi fetchMyPermissions() bị BE từ chối 401, HOẶC khi bất kỳ
  // API nào khác trong app bị 401 (xem onSessionExpired bên dưới) — token còn "hợp lệ" theo kiểm tra
  // client-side (isTokenValid, chỉ decode exp claim) nhưng server đã từ chối thật. Chỉ toast 1 lần
  // đúng lúc false -> true (nhiều API 401 dồn dập cùng lúc không bị spam nhiều toast vì effect chỉ
  // chạy lại khi giá trị dependency thực sự đổi).
  useEffect(() => {
    if (permission.sessionExpired) {
      toast.error(st('phien_lam_viec_cua_ban_da_message'), {
        autoClose: 4000,
      });
      dispatch(logoutUser());
    }
  }, [permission.sessionExpired, dispatch]);

  // Lắng nghe 401 từ BẤT KỲ API call nào trong app (không chỉ fetchMyPermissions) — VD user để tab
  // mở, refresh loop bên dưới lỡ thất bại hoặc token bị revoke phía server giữa chừng, rồi user bấm
  // 1 nút bất kỳ (tạo/sửa/xoá...) và request đó bị 401. Set chung field permission.sessionExpired để
  // tái dùng logic redirect "/login" + logout + toast ở effect ngay trên, không thêm state mới.
  useEffect(() => {
    const unsubscribe = onSessionExpired(() => {
      dispatch(setSessionExpired());
    });
    return unsubscribe;
  }, [dispatch]);

  // Lắng nghe 403 từ BẤT KỲ API call nào trong app (không chỉ route-guard) — nghĩa là quyền vừa bị
  // đổi ở BE trong lúc Redux vẫn đang giữ quyền cũ. Bỏ qua nếu chính API lấy quyền cũng trả 403,
  // tránh gọi lặp vô hạn.
  useEffect(() => {
    if (!userProfile || !token) return;
    const unsubscribe = onForbidden((requestUrl) => {
      if (
        requestUrl &&
        (requestUrl.includes(GET_MY_ALLOWED_PATHS) ||
          requestUrl.includes(GET_MY_PAGE_ACTION_INPUT_CODES))
      ) {
        return;
      }
      dispatch(fetchMyPermissions());
    });
    return unsubscribe;
  }, [userProfile, token, dispatch]);

  useEffect(() => {
    if (userProfile && token) {
      setAuthorization(token);
      const applyLatestToken = () => {
        const latest = getAuthUser();
        const latestToken = latest?.token || latest?.access_token;
        if (latestToken) setAuthorization(latestToken);
      };
      const ensureValidToken = async () => {
        if (!isTokenValid()) {
          const refreshed = await refreshAccessToken();
          if (refreshed) {
            applyLatestToken();
          } else {
            console.log("Refresh token thất bại/hết hạn - logging out");
            dispatch(logoutUser());
          }
        }
      };
      ensureValidToken();
      const intervalId = setInterval(async () => {
        const remainingTime = getTokenRemainingTime();

        if (remainingTime === 0) {
          const refreshed = await refreshAccessToken();
          if (refreshed) {
            applyLatestToken();
          } else {
            console.log("Refresh token thất bại/hết hạn - logging out");
            dispatch(logoutUser());
            clearInterval(intervalId);
          }
        } else if (remainingTime > 0 && remainingTime < 300) {
          console.warn(
            `Token sẽ hết hạn sau ${remainingTime} giây - đang tự động refresh`,
          );
          const refreshed = await refreshAccessToken();
          if (refreshed) {
            applyLatestToken();
          }
        }
      }, 60000);
      return () => clearInterval(intervalId);
    } else if (!loading && !token) {
      dispatch(logoutUser());
    }
  }, [token, userProfile, loading, dispatch]);
  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "100vh" }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">
            {st('dang_kiem_tra_phien_dang_nhap_text')}
          </span>
        </div>
      </div>
    );
  }

  // Redirect về login nếu chưa authenticate
  if (!userProfile || !token) {
    console.log("AuthProtected: No authentication found, redirecting to login");
    return (
      <Navigate
        to={{ pathname: "/login" }}
        state={{ from: location }}
        replace
      />
    );
  }

  if (permission.sessionExpired) {
    console.log(
      "AuthProtected: Token bị BE từ chối (401) - đưa về trang đăng nhập",
      location.pathname,
    );
    return (
      <Navigate to="/login" state={{ from: location }} replace />
    );
  }

  if (permission.loading || (!permission.loaded && !permission.error)) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "100vh" }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">
            {st('dang_xac_thuc_quyen_truy_cap_text')}
          </span>
        </div>
      </div>
    );
  }

  if (!permission.loaded) {
    // fetchMyPermissions() thất bại (không phải 401) — fail-closed, coi như không có quyền thay vì
    // treo UI/tự động retry vô hạn.
    console.error(
      "AuthProtected: Không xác thực được quyền truy cập:",
      permission.error,
    );
    return <Navigate to="/access-denied" replace />;
  }

  if (!ModulePermission.isAllowed(location.pathname, permission)) {
    console.log(
      "AuthProtected: Không có quyền truy cập (đã xác thực với server)",
      location.pathname,
    );
    return <Navigate to="/access-denied" replace />;
  }
  return (
    <>
      {props.children}
      <PageActionScanner />
    </>
  );
};

export default AuthProtected;
